import React, { createContext, useContext, useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getTables, updateTable, joinTables, unjoinTable } from '../../services/TableService';
import { getProducts } from '../../services/ProductService';
import { getCategories } from '../../services/CategoryService';
import {
  getOrder,
  getOrdersByTable,
  addItemsToOrder,
  createOrder,
  createTakeawayOrder,
  sendPendingToKitchen,
  splitOrder,
  updateOrderFulfillment,
  updateItemFulfillmentOverride,
  removeOrderItem,
} from '../../services/OrderService';
import { useAuth } from '../../context/AuthContext';
import { useTakeawayOrders } from '../../hooks/useTakeawayOrders';
import { usePosTransactions } from './hooks/usePosTransactions';
import { computeOrderTotals } from '../../utils/compute-order-totals';
import { safeArray } from '../../utils/safe';
import { trackProductUse, getFrequencyMap } from '../../utils/frequentProducts';
import { FREQUENT_CATEGORY_ID } from '../../utils/posConstants';
import { useCancelOrder } from '../../features/orders/hooks/useCancelOrder';
import type { OrderTicketData } from '../../components/Receipt/OrderTicket';
import type { Table } from '../../types/@types/table';
import type { Product } from '../../types/@types/product';
import type { Order, FulfillmentType, ServeMode, LinePriceCalc } from '../../types/@types/order';
import { echo } from '../../lib/echo';

declare global {
  interface Window {
    Pusher: any;
  }
}

export interface CartItem {
  product: Product;
  qty: number;
  flavors?: { id: string; name: string; qty: number }[];
  additions?: Record<string, number>;
  exceptions?: string[];
  linePriceCalc?: LinePriceCalc;
  kitchenSummary?: string;
  serveMode: ServeMode;
  note?: string;
}

type FlavorFlowTarget = 'cart' | 'order';

interface PosContextValue {
  // Data
  tables: Table[] | undefined;
  products: Product[] | undefined;
  categories: any[] | undefined;
  ordersByTable: Order[] | undefined;
  activeOrder: Order | null;
  loadingTables: boolean;
  loadingProducts: boolean;

  // Selection State
  selectedTable: Table | null;
  setSelectedTable: (table: Table | null) => void;
  activeCategory: string | null;
  setActiveCategory: (id: string | null) => void;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  isTakeawayMode: boolean;

  // CartState
  cart: Record<string, CartItem>;
  setCart: React.Dispatch<React.SetStateAction<Record<string, CartItem>>>;
  activeOrderId: string | number | null;
  setActiveOrderId: (id: string | number | null) => void;
  draftFulfillment: FulfillmentType;
  setDraftFulfillment: (f: FulfillmentType) => void;
  orderCustomer: { customerName: string; customerPhone: string };
  setOrderCustomer: React.Dispatch<React.SetStateAction<{ customerName: string; customerPhone: string }>>;

  // UI Flow State
  flavorContext: { product: Product; target: FlavorFlowTarget } | null;
  setFlavorContext: (ctx: { product: Product; target: FlavorFlowTarget } | null) => void;
  optionsKey: string | null;
  setOptionsKey: (key: string | null) => void;
  mobileTab: 'MENU' | 'ORDER';
  setMobileTab: (tab: 'MENU' | 'ORDER') => void;

  // Visual feedback
  pendingProductId: string | null;

  // Actions
  handleProductClick: (product: Product) => void;
  addProductToCart: (product: Product, flavors?: any[], opts?: any) => void;
  addProductToActiveOrder: (product: Product, payload?: any) => void;
  handleOrder: () => void;
  handleSendPending: () => void;
  handleOrderFulfillmentChange: (next: FulfillmentType) => Promise<void>;
  handleActiveItemFulfillment: (itemId: string, next: FulfillmentType | null) => Promise<void>;
  handleCartItemServeMode: (key: string, next: ServeMode) => void;
  changeQty: (key: string, delta: number) => void;
  removeCartItem: (key: string) => void;

  // Mutations
  addItemsMutation: any;
  sendToKitchenMutation: any;
  splitOrderMutation: any;
  joinTablesMutation: any;
  unjoinTableMutation: any;
  createOrderMutation: any;
  cancelOrderMutation: any;

  // Derived
  filteredProducts: Product[];
  activeOrderItems: any[];
  hasActiveOrder: boolean;
  orderLocked: boolean;
  hasDraft: boolean;
  totals: { subtotal: number; total: number } | null;
  highestKnownSequence: number;
  nextOrderSequence: number;
  activeOrderTotal: number;
  activeOrderTotals: any;
  previousOrdersWithItems: any[];
  activeOrderIdMaybeLoading: boolean;

  // Transaction State (from usePosTransactions)
  cashSession: any;
  showOpenCash: boolean;
  setShowOpenCash: (v: boolean) => void;
  showPayment: boolean;
  setShowPayment: (v: boolean) => void;
  processingPayment: boolean;
  receiptToPrint: any;
  setReceiptToPrint: (v: any) => void;
  orderTicketToPrint: OrderTicketData | null;
  setOrderTicketToPrint: (v: OrderTicketData | null) => void;
  printOrderTicket: () => void;
  lastReceipt: any;
  openingCash: boolean;
  handlePayClick: () => void;
  handleCancelActiveOrder: () => void;
  removeActiveOrderItem: (itemId: string | number) => void;
  removingItem: boolean;
  handleOpenSessionConfirm: (amount: number) => void;
  handleConfirmPayment: (payments: any[]) => void;

  // Helpers
  activeOrderSequence: number | null;
  hasFlavorErrors: boolean;
  hasPendingItems: boolean;
  sendToKitchenMutationIsPending: boolean;
  pendingItemsCount: number;
  splittingOrder: boolean;
  cancelOrderIsPending: boolean;
  createOrderIsPending: boolean;
  buildDetailContentFromOrderItem: (item: any) => React.ReactNode;
  buildDetailContentFromCartItem: (item: CartItem) => React.ReactNode;
}

const PosContext = createContext<PosContextValue | undefined>(undefined);

const TAKEAWAY_TABLE: Table = {
  id: '__takeaway__',
  number: 0,
  capacity: 0,
  status: 'Disponible',
};

export function PosProvider({ mode, children }: { mode: 'TABLE' | 'TAKEAWAY', children: React.ReactNode }) {
  const isTakeawayMode = mode === 'TAKEAWAY';
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const cancelOrderMutation = useCancelOrder();

  // --- BASIC STATE ---
  const [selectedTable, setSelectedTableInternal] = useState<Table | null>(isTakeawayMode ? TAKEAWAY_TABLE : null);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [orderTicketToPrint, setOrderTicketToPrint] = useState<OrderTicketData | null>(null);
  const [cartByTable, setCartByTable] = useState<Record<string, Record<string, CartItem>>>({});
  const [activeOrderId, setActiveOrderId] = useState<string | number | null>(null);
  const [draftFulfillment, setDraftFulfillment] = useState<FulfillmentType>('DINE_IN');
  const [pendingProductId, setPendingProductId] = useState<string | null>(null);
  const [orderCustomer, setOrderCustomer] = useState({ customerName: '', customerPhone: '' });
  const [flavorContext, setFlavorContext] = useState<{ product: Product; target: FlavorFlowTarget } | null>(null);
  const [optionsKey, setOptionsKey] = useState<string | null>(null);
  const [mobileTab, setMobileTab] = useState<'MENU' | 'ORDER'>('MENU');
  const [showCancelModal, setShowCancelModal] = useState(false);

  // --- QUERIES ---
  const { data: tables, isLoading: loadingTables, refetch: refetchTables } = useQuery({
    queryKey: ['posTables'],
    queryFn: getTables,
    enabled: !isTakeawayMode,
  });

  const { data: products, isLoading: loadingProducts } = useQuery({
    queryKey: ['posProducts'],
    queryFn: getProducts,
    enabled: isTakeawayMode || !!selectedTable,
  });

  const { data: categories } = useQuery({
    queryKey: ['posCategories'],
    queryFn: getCategories,
    enabled: isTakeawayMode || !!selectedTable,
  });

  const { data: ordersByTable } = useQuery({
    queryKey: ['ordersByTable', selectedTable?.id],
    queryFn: () => getOrdersByTable(selectedTable!.id),
    enabled: !isTakeawayMode && !!selectedTable?.id,
  });

  const { data: activeOrder, refetch: refetchActiveOrder, error: activeOrderError } = useQuery({
    queryKey: ['activeOrder', activeOrderId],
    queryFn: () => (activeOrderId ? getOrder(activeOrderId) : null),
    enabled: !!activeOrderId,
  });

  // --- HELPERS ---
  const formatAdditions = useCallback((raw: any) => {
    if (!raw) return undefined;
    if (Array.isArray(raw)) {
      return raw.map((entry: any) => {
        if (typeof entry === 'string') return entry;
        const qty = entry?.quantity ?? entry?.qty;
        const label = entry?.name ?? entry?.label ?? entry?.title;
        if (label) return qty && qty > 1 ? `${qty}x ${label}` : label;
        return String(entry);
      });
    }
    return [String(raw)];
  }, []);

  const mapOrderItems = useCallback((order?: Order | null) => {
    const list = ((order?.items ?? (order as any)?.products) ?? []) as any[];
    return list.map((item: any) => {
      const productDetails = typeof item.product === 'string' ? products?.find((p) => p.id === item.product) : item.product;
      const base_price = item.basePrice ?? item.base_price ?? (productDetails as Product | undefined)?.price ?? 0;
      const line_subtotal = item.lineSubtotal ?? item.line_subtotal ?? (typeof item.quantity === 'number' ? item.quantity * base_price : 0);
      const unitPrice = item.quantity > 0 ? line_subtotal / item.quantity : base_price;
      return {
        ...item,
        product: productDetails,
        name: item.name || productDetails?.name,
        base_price, line_subtotal, unitPrice,
        formattedAdditions: formatAdditions(item.additions),
        status: (item.status || item.itemStatus || '').toUpperCase()
      };
    });
  }, [products, formatAdditions]);

  // --- CART MANAGEMENT ---
  const cart = useMemo(() => (selectedTable ? cartByTable[String(selectedTable.id)] || {} : {}), [selectedTable, cartByTable]);

  const internalSetCart = useCallback((val: any) => {
    if (!selectedTable) return;
    setCartByTable(prev => ({
      ...prev,
      [String(selectedTable.id)]: typeof val === 'function' ? val(prev[String(selectedTable.id)] || {}) : val
    }));
  }, [selectedTable]);

  const changeQty = useCallback((key: string, delta: number) => {
    internalSetCart((prev: any) => {
      const item = prev[key];
      if (!item) return prev;
      const newQty = item.qty + delta;
      if (newQty <= 0) {
        const { [key]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [key]: { ...item, qty: newQty } };
    });
  }, [internalSetCart]);

  const removeCartItem = useCallback((key: string) => {
    internalSetCart((prev: any) => {
      const { [key]: _, ...rest } = prev;
      return rest;
    });
  }, [internalSetCart]);

  const selectedTableRef = useRef(selectedTable);
  useEffect(() => {
    selectedTableRef.current = selectedTable;
  }, [selectedTable]);

  // --- TABLA SWITCH — reset activeOrderId y searchTerm al cambiar de mesa ---
  const handleSetSelectedTable = useCallback((table: Table | null) => {
    if (table?.id !== selectedTable?.id) {
      setActiveOrderId(null);
      setSearchTerm('');
    }
    setSelectedTableInternal(table);
  }, [selectedTable?.id]);

  // --- REAL-TIME UPDATES ---
  useEffect(() => {
    // Escuchamos actualizaciones de mesas (liberación, unión, etc)
    const tableChannel = echo.channel('pos-updates');

    tableChannel.listen('TableUpdated', (e: any) => {
      console.log('Echo: Mesa actualizada', e);
      queryClient.invalidateQueries({ queryKey: ['posTables'] });
      // Si es la mesa que tenemos abierta, refrescar también sus órdenes
      if (selectedTableRef.current?.id == (e.table?.id || e.tableId)) {
        refetchActiveOrder();
        queryClient.invalidateQueries({ queryKey: ['ordersByTable', selectedTableRef.current?.id] });
      }
    });

    // Escuchamos actualizaciones de pedidos (cocina)
    const orderChannel = echo.channel('orders');

    orderChannel.listen('.orders.updated', (e: any) => {
      console.log('Echo: Pedido actualizado', e);
      if (selectedTableRef.current?.id == e.order?.table_id) {
        refetchActiveOrder();
      }
    });

    orderChannel.listen('.orders.new', (e: any) => {
      console.log('Echo: Pedido nuevo', e);
      if (selectedTableRef.current?.id == e.order?.table_id) {
        refetchActiveOrder();
      }
    });

    return () => {
      echo.leave('pos-updates');
      echo.leave('orders');
    };
  }, [refetchTables, refetchActiveOrder, queryClient]);


  // --- MUTATIONS ---
  const createOrderMutation = useMutation({
    mutationFn: (data: any) => createOrder(data),
    onSuccess: (response: any) => {
      const data = response.data || response;
      toast.success('Pedido creado con éxito');

      // 1. Alimentar la caché de activeOrder al instante (evita espera de red)
      queryClient.setQueryData(['activeOrder', data.id], data);
      queryClient.setQueryData(['activeOrder', String(data.id)], data);

      // 2. CRÍTICO: insertar la nueva orden en la caché de ordersByTable ANTES de
      //    setActiveOrderId, para que el efecto de auto-selección la encuentre y
      //    no resetee activeOrderId a null (condición de carrera).
      if (!isTakeawayMode && selectedTable?.id) {
        const tableKey = ['ordersByTable', selectedTable.id];
        const current = queryClient.getQueryData<any[]>(tableKey) ?? [];
        if (!current.find((o: any) => String(o.id) === String(data.id))) {
          queryClient.setQueryData(tableKey, [...current, data]);
        }
      }

      setActiveOrderId(data.id);
      internalSetCart({});
      refetchTables();
      // Refrescar ordersByTable para que lleguen datos frescos del backend
      queryClient.invalidateQueries({ queryKey: ['ordersByTable', selectedTable?.id] });
      if (isTakeawayMode) {
        window.location.hash = '#/pos';
      }
    },
    onError: () => toast.error('Error al crear el pedido')
  });

  const addItemsMutation = useMutation({
    mutationFn: ({ orderId, items }: { orderId: string | number, items: any[] }) => addItemsToOrder(String(orderId), { items }),
    onSuccess: (response: any) => {
      const data = response.data || response;
      toast.success('Productos añadidos');

      // Alimentar la caché al instante con la respuesta del backend
      if (activeOrderId) {
        queryClient.setQueryData(['activeOrder', activeOrderId], data);
        queryClient.setQueryData(['activeOrder', String(activeOrderId)], data);
        queryClient.setQueryData(['activeOrder', Number(activeOrderId)], data);
      }

      internalSetCart({});
      refetchActiveOrder();
    },
    onError: () => toast.error('Error al añadir productos')
  });

  const removeItemMutation = useMutation({
    mutationFn: ({ orderId, itemId }: { orderId: string | number; itemId: string | number }) =>
      removeOrderItem(orderId, itemId),
    onSuccess: (data: any) => {
      toast.success('Producto quitado del pedido');
      if (activeOrderId) {
        queryClient.setQueryData(['activeOrder', activeOrderId], data);
        queryClient.setQueryData(['activeOrder', String(activeOrderId)], data);
        queryClient.setQueryData(['activeOrder', Number(activeOrderId)], data);
      }
      refetchActiveOrder();
      queryClient.invalidateQueries({ queryKey: ['ordersByTable', selectedTableRef.current?.id] });
    },
    onError: (e: any) => toast.error(e.response?.data?.message ?? 'No se pudo quitar el producto'),
  });

  const removeActiveOrderItem = (itemId: string | number) => {
    if (!activeOrderId) return;
    removeItemMutation.mutate({ orderId: activeOrderId, itemId });
  };

  const sendToKitchenMutation = useMutation({
    mutationFn: (orderId: string | number) => sendPendingToKitchen(String(orderId)),
    onSuccess: (response: any) => {
      const data = response.data || response;
      toast.success('Enviado a cocina');

      // Alimentar la caché al instante
      if (activeOrderId) {
        queryClient.setQueryData(['activeOrder', activeOrderId], data);
        queryClient.setQueryData(['activeOrder', String(activeOrderId)], data);
        queryClient.setQueryData(['activeOrder', Number(activeOrderId)], data);
      }

      refetchActiveOrder();
    }
  });

  const joinTablesMutation = useMutation({
    mutationFn: ({ primaryId, secondaryId }: { primaryId: string | number, secondaryId: string | number }) => joinTables(String(primaryId), String(secondaryId)),
    onSuccess: () => {
      toast.success('Mesas unidas');
      refetchTables();
    }
  });

  const unjoinTableMutation = useMutation({
    mutationFn: (id: string | number) => unjoinTable(String(id)),
    onSuccess: () => {
      toast.success('Mesa desvinculada');
      refetchTables();
    }
  });

  const splitOrderMutation = useMutation({
    mutationFn: ({ orderId, itemIds }: { orderId: string | number, itemIds: string[] }) => splitOrder(String(orderId), itemIds),
    onSuccess: () => {
      toast.success('Pedido dividido con éxito');
      refetchActiveOrder();
    }
  });

  // --- DERIVED DATA ---
  const activeOrderItems = useMemo(() => mapOrderItems(activeOrder), [activeOrder, mapOrderItems]);
  const activeOrderTotals = useMemo(() => {
    const items = activeOrderItems.map(i => ({ qty: i.quantity, unitPrice: i.unitPrice }));
    return computeOrderTotals(items);
  }, [activeOrderItems]);
  const activeOrderTotal = activeOrderTotals.total;

  const hasActiveOrder = useMemo(() => {
    // Una orden está activa si no ha sido PAGADA ni CANCELADA.
    // Usamos payment_status para el estado de pago y status para cocina.
    const isPaid = activeOrder?.payment_status === 'PAID' || activeOrder?.paid === true;
    const isCancelled = activeOrder?.status?.toUpperCase() === 'CANCELLED';
    return !!activeOrder && !isPaid && !isCancelled;
  }, [activeOrder]);

  const sortedTableOrders = useMemo(() => {
    if (!ordersByTable) return [];
    return ordersByTable
      .filter(o => {
        const isPaid = o.payment_status === 'PAID' || o.paid === true;
        const isCancelled = (o.status || '').toUpperCase() === 'CANCELLED';
        return !isPaid && !isCancelled;
      })
      .sort((a,b) => (a.orderSequence ?? 0) - (b.orderSequence ?? 0));
  }, [ordersByTable]);

  const previousOrdersWithItems = useMemo(() => {
    return sortedTableOrders
      .filter(o => o.id !== activeOrder?.id)
      .map(o => ({
        order: o,
        items: mapOrderItems(o),
        totals: computeOrderTotals(mapOrderItems(o).map(i => ({ qty: i.quantity, unitPrice: i.unitPrice }))),
        sequence: o.orderSequence ?? 0
      }));
  }, [sortedTableOrders, activeOrder, mapOrderItems]);

  const highestKnownSequence = useMemo(() => {
    const sequences = (ordersByTable || []).map(o => o.orderSequence).filter(s => typeof s === 'number') as number[];
    return sequences.length > 0 ? Math.max(...sequences) : (ordersByTable?.length || 0);
  }, [ordersByTable]);

  const nextOrderSequence = highestKnownSequence + 1;
  const totals = useMemo(() => {
    if (hasActiveOrder) return activeOrderTotals;
    const items = Object.values(cart).map(c => ({
      qty: c.qty,
      unitPrice: (c.linePriceCalc?.final ?? 0) / c.qty || c.product.price + (c.serveMode === 'TAKEAWAY' ? (c.product.takeawayPrice || 0) : 0)
    }));
    return computeOrderTotals(items);
  }, [hasActiveOrder, activeOrderTotals, cart]);

  // --- AUTO-SELECT ACTIVE ORDER ---
  useEffect(() => {
    if (sortedTableOrders.length > 0) {
      // Si no hay orden activa, o la orden activa no pertenece a las órdenes de esta mesa,
      // seleccionamos la última. Si hay un activeOrderId seteado pero ordersByTable todavía
      // no lo tiene (caché stale justo después de crear), NO lo reseteamos — el siguiente
      // ciclo lo encontrará una vez que el caché se actualice.
      const foundInList = sortedTableOrders.find(o => String(o.id) === String(activeOrderId));
      if (!activeOrderId || !foundInList) {
        setActiveOrderId(sortedTableOrders[sortedTableOrders.length - 1].id);
      }
    } else if (ordersByTable !== undefined && sortedTableOrders.length === 0) {
      // ordersByTable ya cargó y no hay órdenes activas para esta mesa → limpiar
      // (la condición de carrera tras crear una orden está resuelta porque onSuccess
      // ya inserta la nueva orden en la caché de ordersByTable antes de setActiveOrderId)
      if (activeOrderId) setActiveOrderId(null);
    }
  }, [sortedTableOrders, activeOrderId, ordersByTable]);

  // --- ACTIONS ---
  const addProductToCart = useCallback((product: Product, flavors: any[] = [], opts: any = {}) => {
    const key = `${product.id}-${Date.now()}`;
    const newItem: CartItem = {
      product,
      qty: 1,
      flavors,
      additions: opts.additions || {},
      exceptions: opts.exceptions || [],
      serveMode: draftFulfillment === 'DINE_IN' ? 'MESA' : 'TAKEAWAY',
    };

    internalSetCart((prev: any) => ({ ...prev, [key]: newItem }));
    setPendingProductId(String(product.id));
    setTimeout(() => setPendingProductId(null), 600);
    toast.success(`✓ ${product.name} agregado`, { autoClose: 1500, hideProgressBar: true });
  }, [draftFulfillment, internalSetCart]);

  const addProductToActiveOrder = useCallback(async (product: Product, payload: any = {}) => {
    if (!activeOrderId) return;

    // Transformamos sabores si existen en el payload
    const formattedFlavors = safeArray(payload.flavors).map((f: any) => ({
      flavorId: f.id,
      qty: f.qty
    }));

    setPendingProductId(String(product.id));
    addItemsMutation.mutate({
      orderId: activeOrderId,
      items: [{
        product: product.id,
        quantity: 1,
        ...payload,
        flavors: formattedFlavors
      }]
    }, {
      onSettled: () => setTimeout(() => setPendingProductId(null), 600),
    });
    toast.success(`✓ ${product.name} agregado`, { autoClose: 1500, hideProgressBar: true });
  }, [activeOrderId, addItemsMutation]);

  const handleProductClick = useCallback((product: Product) => {
    // Registrar uso para la categoría "Frecuentes"
    trackProductUse(product.id);

    const needsConfig = (product.maxFlavors && product.maxFlavors > 0) ||
                       product.flavors?.enabled || product.additions?.enabled || product.exceptions?.enabled;

    if (needsConfig) {
      setFlavorContext({ product, target: hasActiveOrder ? 'order' : 'cart' });
    } else {
      if (hasActiveOrder) addProductToActiveOrder(product);
      else addProductToCart(product);
    }
  }, [hasActiveOrder, addProductToActiveOrder, addProductToCart]);

  const handleOrder = useCallback(() => {
    if (Object.keys(cart).length === 0) return;
    const items = Object.values(cart).map(item => ({
      product: item.product.id,
      quantity: item.qty,
      flavors: safeArray(item.flavors).map(f => ({
        flavorId: f.id,
        qty: f.qty
      })),
      additions: item.additions,
      exceptions: item.exceptions,
      serveMode: item.serveMode,
      notes: item.note || undefined,
    }));

    createOrderMutation.mutate({
      tableId: isTakeawayMode ? null : selectedTable?.id,
      destination: isTakeawayMode ? 'TAKEAWAY' : undefined,
      items,
      fulfillmentType: draftFulfillment,
      customerName: orderCustomer.customerName,
      customerPhone: orderCustomer.customerPhone,
    });
  }, [cart, isTakeawayMode, selectedTable, draftFulfillment, orderCustomer, createOrderMutation]);

  // Construye la comanda a partir de los ítems de la orden (todos o solo pendientes)
  const buildComanda = useCallback((onlyPending: boolean): OrderTicketData | null => {
    const all = activeOrderItems;
    if (!all.length) return null;
    const pending = all.filter(i => ['PENDING', 'WAITING', ''].includes((i.status || '').toUpperCase()));
    const source = onlyPending ? (pending.length ? pending : all) : all;
    const items = source.map((i: any) => ({
      qty: i.quantity || 1,
      name: i.name || i.product?.name || 'Producto',
      notes: i.notes || undefined,
      extras: [
        ...((i.itemFlavors || i.item_flavors || []).map((f: any) =>
          `${(f.qty ?? 1) > 1 ? (f.qty + 'x ') : ''}${f.flavor?.name ?? f.name ?? ''}`.trim()).filter(Boolean)),
        ...(Array.isArray(i.formattedAdditions) ? i.formattedAdditions : []),
      ],
    }));
    const title = isTakeawayMode
      ? 'PARA LLEVAR'
      : (selectedTable?.number != null ? `MESA ${selectedTable.number}` : (activeOrder?.table_id ? `MESA ${activeOrder.table_id}` : 'PEDIDO'));
    return { title, orderNumber: activeOrder?.orderSequence ?? activeOrderId ?? undefined, time: new Date().toISOString(), items };
  }, [activeOrderItems, isTakeawayMode, selectedTable, activeOrder, activeOrderId]);

  // Botón manual: imprimir comanda de la orden actual
  const printOrderTicket = useCallback(() => {
    const comanda = buildComanda(false);
    if (!comanda || !comanda.items.length) { toast.info('No hay productos en la orden'); return; }
    setOrderTicketToPrint(comanda);
  }, [buildComanda]);

  const handleSendPending = useCallback(() => {
    if (!activeOrderId) return;
    const comanda = buildComanda(true);
    sendToKitchenMutation.mutate(activeOrderId, {
      onSuccess: () => { if (comanda && comanda.items.length) setOrderTicketToPrint(comanda); },
    });
  }, [activeOrderId, buildComanda, sendToKitchenMutation]);

  const handleOrderFulfillmentChange = async (next: FulfillmentType) => {
    if (!activeOrder) return;
    await updateOrderFulfillment(activeOrder.id, next);
    refetchActiveOrder();
    toast.success('Entrega actualizada');
  };

  const handleActiveItemFulfillment = async (itemId: string, next: FulfillmentType | null) => {
    if (!activeOrder) return;
    await updateItemFulfillmentOverride(activeOrder.id, itemId, next);
    refetchActiveOrder();
  };

  const handleCartItemServeMode = (key: string, next: ServeMode) => {
    internalSetCart((prev: any) => ({
      ...prev,
      [key]: { ...prev[key], serveMode: next }
    }));
  };

  // --- TRANSACTIONS ---
  const trans = usePosTransactions({
    isTakeawayMode, activeOrder, activeOrderTotal, activeOrderItems, activeOrderTotals,
    selected: selectedTable, tables, user, orderCustomer,
    cancelOrderMutation, unjoinTableMutation: unjoinTableMutation as any, updateTableMutation: {} as any,
    setCancelModalOpen: () => setShowCancelModal(true), setActiveOrderId, setCart: internalSetCart, refetchActiveOrder, refetchTables, setSelected: handleSetSelectedTable
  });

  const value: PosContextValue = {
    tables, products, categories, ordersByTable, activeOrder, loadingTables, loadingProducts,
    selectedTable, setSelectedTable: handleSetSelectedTable, activeCategory, setActiveCategory, searchTerm, setSearchTerm, isTakeawayMode,
    cart, setCart: internalSetCart, activeOrderId, setActiveOrderId, draftFulfillment, setDraftFulfillment, orderCustomer, setOrderCustomer,
    flavorContext, setFlavorContext, optionsKey, setOptionsKey, mobileTab, setMobileTab,
    pendingProductId,
    handleProductClick, handleOrder, handleSendPending, addProductToCart, addProductToActiveOrder,
    handleOrderFulfillmentChange, handleActiveItemFulfillment,
    handleCartItemServeMode, changeQty, removeCartItem,
    addItemsMutation, sendToKitchenMutation, splitOrderMutation, joinTablesMutation, unjoinTableMutation, createOrderMutation, cancelOrderMutation,
    removeActiveOrderItem, removingItem: removeItemMutation.isPending,
    orderTicketToPrint, setOrderTicketToPrint, printOrderTicket,
    filteredProducts: (() => {
      const term = searchTerm.toLowerCase();
      const base = safeArray(products).filter(p => p.name.toLowerCase().includes(term));

      // Categoría especial: Frecuentes (ordenado por uso local)
      if (activeCategory === FREQUENT_CATEGORY_ID) {
        const freq = getFrequencyMap();
        return base
          .filter(p => (freq[String(p.id)] ?? 0) > 0)
          .sort((a, b) => (freq[String(b.id)] ?? 0) - (freq[String(a.id)] ?? 0));
      }

      if (!activeCategory) return base;
      return base.filter(p => {
        const catId = typeof p.category === 'string' ? p.category : p.category?.id;
        return catId === activeCategory;
      });
    })(),
    activeOrderItems, hasActiveOrder, orderLocked: activeOrder?.status !== 'WAITING', hasDraft: Object.keys(cart).length > 0,
    totals, highestKnownSequence, nextOrderSequence, activeOrderTotal, activeOrderTotals, previousOrdersWithItems,
    activeOrderIdMaybeLoading: !!activeOrderId && !activeOrderError,
    ...trans,
    buildDetailContentFromOrderItem: (item) => <span>{item.name}</span>,
    buildDetailContentFromCartItem: (item) => <span>{item.product.name}</span>,
    activeOrderSequence: activeOrder?.orderSequence ?? null,
    hasFlavorErrors: false,
    hasPendingItems: activeOrderItems.some(i => i.status === 'PENDING' || i.status === 'WAITING'),
    sendToKitchenMutationIsPending: sendToKitchenMutation.isPending,
    pendingItemsCount: activeOrderItems.filter(i => i.status === 'PENDING' || i.status === 'WAITING').length,
    splittingOrder: splitOrderMutation.isPending,
    cancelOrderIsPending: cancelOrderMutation.isPending,
    createOrderIsPending: createOrderMutation.isPending,
  };

  return <PosContext.Provider value={value}>{children}</PosContext.Provider>;
}

export function usePos() {
  const context = useContext(PosContext);
  if (!context) throw new Error('usePos must be used within PosProvider');
  return context;
}
